import { Navbar } from "@/components/Navbar";
import { LiveTipCard } from "@/components/LiveTipCard";
import { Button } from "@/components/ui/button";
import { Loader2, Radio, TrendingUp } from "lucide-react";
import { useLiveTips } from "@/hooks/useLiveTips";
import { motion } from "framer-motion";

export default function LiveTips() {
  const { data, isLoading, isError, error } = useLiveTips();

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/30">
      <Navbar />

      <main className="py-24 container px-4">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="animate-ping absolute h-4 w-4 rounded-full bg-red-500 opacity-75"></div>
              <div className="relative h-4 w-4 rounded-full bg-red-500"></div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">
              LIVE <span className="text-primary">TIPS</span>
            </h1>
          </div>
          <p className="text-muted-foreground text-lg">
            Real-time betting opportunities as matches unfold. Tips auto-update every 30 seconds.
          </p>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass p-6 rounded-lg border border-white/5"
          >
            <div className="flex items-center gap-3">
              <Radio className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">Active Tips</p>
                <p className="text-2xl font-bold text-white">{data?.count || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass p-6 rounded-lg border border-white/5"
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Avg Confidence</p>
                <p className="text-2xl font-bold text-white">
                  {data?.tips.length
                    ? Math.round(
                        data.tips.reduce((acc, tip) => acc + tip.confidence, 0) / data.tips.length
                      )
                    : 0}
                  %
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass p-6 rounded-lg border border-white/5"
          >
            <div className="flex items-center gap-3">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              <div>
                <p className="text-sm text-muted-foreground">Updates</p>
                <p className="text-2xl font-bold text-white">Every 30s</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Live Tips Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading live tips...</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-500 mb-4 text-6xl">⚠️</div>
            <h3 className="text-xl font-bold mb-2">Failed to Load Live Tips</h3>
            <p className="text-muted-foreground mb-6">
              {error instanceof Error ? error.message : 'Please try again later.'}
            </p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : !data?.tips || data.tips.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-muted-foreground mb-4 text-6xl">📊</div>
            <h3 className="text-xl font-bold mb-2">No Live Tips Available</h3>
            <p className="text-muted-foreground mb-2">
              Live tips are generated during active matches (15' - 85')
            </p>
            <p className="text-sm text-muted-foreground">
              Check back when matches are in progress
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.tips.map((tip, index) => (
              <motion.div
                key={tip.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <LiveTipCard tip={tip} />
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Section */}
        {data && data.tips.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 glass p-6 rounded-lg border border-white/5"
          >
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              <span className="text-primary">💡</span> How Live Tips Work
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Tips are generated opportunistically during live matches based on high-confidence situations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Each tip expires after 5 minutes or when the predicted event passes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Confidence is calculated based on match momentum, recent events, and scoring patterns</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Free users see free live tips. Upgrade to Pro for premium live markets</span>
              </li>
            </ul>
          </motion.div>
        )}
      </main>
    </div>
  );
}
